import { inject, Injectable } from '@angular/core';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { Observable, of, take, tap } from 'rxjs';
import { LorenzFormComponentDisplayOptions, LorenzFormControl, LorenzFormGroup, LorenzFormLayoutEnum, LorenzFormService } from '@lib/forms';
import { LibRequestService, RequestOptions } from '@lib/request';
import { LibSharedService } from '@lib/shared';
import { TranslateService } from '@ngx-translate/core';

registerLocaleData(localeDe);
registerLocaleData(localeEn);

@Injectable({
	providedIn: 'root'
})
export class AppService {
	private readonly document = inject(DOCUMENT);
	private readonly translateService = inject(TranslateService);

	private readonly libSharedService = inject(LibSharedService);
	public readonly libRequestService = inject(LibRequestService);
	private readonly lorenzFormService = inject(LorenzFormService);

	public requestOptions: RequestOptions | null = null;
	public requestForm: Observable<LorenzFormGroup | null> = of(null);
	private requestFormValue: LorenzFormGroup | null = null;
	public requestFormLoadFailed = false;

	public formOptions: LorenzFormComponentDisplayOptions = {
		layout: LorenzFormLayoutEnum.FLOATING_LABELS,
		showErrorMessages: false
	};

	public appStatus: 'EDIT' | 'SUCCESS' | 'FAILURE' | null = null; // Aktueller Status der App.

	storeRequestData(requestForm: LorenzFormGroup) {
		return this.libRequestService.storeRequestData(requestForm.getRawValue(), this.getTranslation('Anfrage wird weitergeleitet')).pipe(
			tap((result) => {
				this.appStatus = result ? 'SUCCESS' : 'FAILURE';
			})
		);
	}

	getGroup(lorenzFormGroupName?: string): LorenzFormGroup | null {
		if (!this.requestFormValue || !lorenzFormGroupName) {
			return this.requestFormValue;
		}
		return this.lorenzFormService.getLorenzFormGroup(this.requestFormValue, lorenzFormGroupName);
	}

	getControl(lorenzFormControlName?: string): LorenzFormControl | null {
		if (!this.requestFormValue || !lorenzFormControlName) {
			return null;
		}
		return this.lorenzFormService.getLorenzFormControl(this.requestFormValue, lorenzFormControlName);
	}

	debug() {
		console.log('requestForm', this.requestFormValue ? this.requestFormValue.value : '*** NO FORM ***');
	}

	resetApp() {
		this.requestFormValue = null;
		this.requestForm = of(null);
		this.requestFormLoadFailed = false;
		this.appStatus = null;

		if (!this.requestOptions) {
			return;
		}

		this.requestForm = this.getRequestForm(this.requestOptions);

		if (this.requestForm) {
			this.appStatus = 'EDIT';
		}
	}

	retryRequestForm(): void {
		if (!this.requestOptions) {
			return;
		}

		this.requestFormValue = null;
		this.requestFormLoadFailed = false;
		this.requestForm = this.getRequestForm(this.requestOptions);
	}

	private getRequestForm(requestOptions: RequestOptions) {
		console.log('getRequestForm');
		// prettier-ignore
		return this.libRequestService.newRequestForm(requestOptions, this.getTranslation('Formular wird abgerufen'))
			.pipe(
				take(1),
				tap((requestForm) => {
					this.requestFormValue = requestForm ?? null;
					this.requestFormLoadFailed = !requestForm;
				})
			);
	}

	// ----- Language related --------------------------------------------------------------------------------------------

	setLanguage(lang: string) {
		this.libSharedService.setLanguage(lang);
		this.document.documentElement.lang = this.libSharedService.getCurrentLanguage();
	}

	getCurrentLanguage = this.libSharedService.getCurrentLanguage;

	getDefaultLanguage = this.libSharedService.getDefaultLanguage;

	getTranslation(key: string): string {
		// const help = this.getCurrentLanguage();
		return this.translateService.instant(key);
	}
}
